import { Button, ConfirmDialog, DataTable, EmptyState, InlineMessage, Spinner } from '@olytic/foundation';
import { useEffect, useState } from 'react';

import { authenticatedFetch } from '../auth/authClient';
import { objects } from '../config/objects';
import { useDocumentTitle } from '../documentTitle';
import { SetupPage } from '../setup/SetupPages';
import { DerivedFieldDialog } from './DerivedFieldDialog';
import {
  deleteField,
  type DerivedField,
  fetchFields,
  OUTPUT_TYPE_LABELS,
  progressText,
  rerunField,
  updateField,
} from './derivedApi';

/**
 * Setup → Fields from history: every derived field, what it asks and how far
 * its backfill has got. New, edit, pause, re-run and delete.
 */
export function FieldsFromHistoryPage(): JSX.Element {
  useDocumentTitle('Fields from history');
  const [fields, setFields] = useState<DerivedField[] | null>(null);
  const [version, setVersion] = useState(0);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ tone: 'error' | 'success'; text: string } | null>(null);
  const [editing, setEditing] = useState<DerivedField | 'new' | null>(null);
  const [toDelete, setToDelete] = useState<DerivedField | null>(null);
  const reload = () => setVersion((n) => n + 1);

  useEffect(() => {
    const controller = new AbortController();
    fetchFields(authenticatedFetch, undefined, { signal: controller.signal }).then(
      (result) => { if (!controller.signal.aborted) { setFields(result); setLoadError(null); } },
      (error: unknown) => { if (!controller.signal.aborted) setLoadError(error instanceof Error ? error.message : 'Could not load fields.'); },
    );
    return () => controller.abort();
  }, [version]);

  async function act(work: () => Promise<string>) {
    setNotice(null);
    try {
      setNotice({ tone: 'success', text: await work() });
      reload();
    } catch (error) {
      setNotice({ tone: 'error', text: error instanceof Error ? error.message : 'That did not work.' });
    }
  }

  return (
    <SetupPage title="Fields from history">
      <InlineMessage tone="info">
        A field from history is a question answered for every record from its meetings, transcripts and email. Add one
        and every existing record is filled by the Mothership server's AI; new history keeps it current.
      </InlineMessage>
      {notice ? <InlineMessage tone={notice.tone} role={notice.tone === 'error' ? 'alert' : 'status'}>{notice.text}</InlineMessage> : null}
      <div className="flex justify-end">
        <Button variant="brand" onClick={() => setEditing('new')}>New field</Button>
      </div>
      {loadError ? (
        <InlineMessage tone="error" role="alert">{loadError}</InlineMessage>
      ) : fields === null ? (
        <Spinner label="Loading fields…" />
      ) : (
        <DataTable<DerivedField>
          columns={[
            { key: 'label', label: 'Name', primary: true },
            { key: 'object', label: 'Object', get: (f) => objects[f.object].plural },
            { key: 'output_type', label: 'Answer Type', get: (f) => OUTPUT_TYPE_LABELS[f.output_type] },
            { key: 'instruction', label: 'What to Find' },
            { key: 'progress', label: 'Progress', get: (f) => (f.status === 'paused' ? `Paused · ${progressText(f)}` : progressText(f)) },
          ]}
          rows={fields}
          getRowId={(f) => f.id}
          onRowClick={(f) => setEditing(f)}
          rowActionsTrigger="chevron"
          rowActions={[
            { label: 'Edit', onSelect: (f) => setEditing(f) },
            {
              label: 'Pause',
              hidden: (f) => f.status === 'paused',
              onSelect: (f) => void act(async () => { await updateField(authenticatedFetch, f.id, { status: 'paused' }); return `Paused ${f.label}.`; }),
            },
            {
              label: 'Resume',
              hidden: (f) => f.status === 'active',
              onSelect: (f) => void act(async () => { await updateField(authenticatedFetch, f.id, { status: 'active' }); return `Resumed ${f.label}.`; }),
            },
            {
              label: 'Re-run for every record',
              onSelect: (f) => void act(async () => {
                const queued = await rerunField(authenticatedFetch, f.id);
                return `Queued ${queued.toLocaleString('en-US')} ${queued === 1 ? 'value' : 'values'} of ${f.label}. Values people set are kept.`;
              }),
            },
            { label: 'Delete', destructive: true, onSelect: (f) => setToDelete(f) },
          ]}
          empty={<EmptyState title="No fields from history yet" description="Add one here, or with AI column on any list." />}
        />
      )}
      <DerivedFieldDialog
        open={editing !== null}
        onOpenChange={(open) => { if (!open) setEditing(null); }}
        {...(editing && editing !== 'new' ? { field: editing } : {})}
        onSaved={(field) => {
          setEditing(null);
          setNotice({ tone: 'success', text: `Saved ${field.label}. ${progressText(field)}.` });
          reload();
        }}
      />
      <ConfirmDialog
        open={toDelete !== null}
        onOpenChange={(open) => { if (!open) setToDelete(null); }}
        title={`Delete ${toDelete?.label ?? ''}?`}
        description="Its values on every record are deleted too, including any a person set."
        confirmLabel="Delete"
        tone="danger"
        onConfirm={() => {
          const field = toDelete;
          setToDelete(null);
          if (field) void act(async () => { await deleteField(authenticatedFetch, field.id); return `Deleted ${field.label}.`; });
        }}
      />
    </SetupPage>
  );
}
